const DRAFT_NOTES_PREFIX = "Building Permit Application (Draft): ";
const SUBMITTED_NOTES_PREFIX = "Building Permit Application (Submitted): ";
const MODAL_FORM_DATA_KEYS = [
    "applicantRole",
    "applicantAcnArbn",
    "contactName",
    "email",
    "companyName",
    "phone",
    "address",
    "addressForServing",
    "isLiaseeOrLicensee",
    "ownerName",
    "ownerContactPerson",
    "ownerEmail",
    "ownerCompanyName",
    "ownerPhone",
    "ownerAddress",
    "acnArbn",
    "lesseeResponsible",
    "tenantContactPerson",
    "tenantEmail",
    "tenantCompanyName",
    "tenantPhone",
    "tenantAddress",
    "builderName",
    "builderContactPerson",
    "builderEmail",
    "builderCompanyName",
    "builderPhone",
    "builderAddress",
    "builderPractitionerNo",
    "builderAcnArbn",
    "builderPostalAddress",
];
function parseJsonFromNotes(notes, prefix) {
    if (!notes || !notes.includes(prefix))
        return null;
    const jsonStart = notes.indexOf(prefix) + prefix.length;
    const jsonStr = notes.slice(jsonStart);
    try {
        return JSON.parse(jsonStr);
    }
    catch {
        return null;
    }
}
function extractModalFormData(parsed) {
    const result = {};
    for (const key of MODAL_FORM_DATA_KEYS) {
        if (key in parsed) {
            const v = parsed[key];
            if (typeof v === "string" || typeof v === "boolean") {
                result[key] = v;
            }
        }
    }
    return result;
}
function extractAdditionalFormData(parsed) {
    const additional = {};
    let hasAdditional = false;
    if (parsed.propertyDetails && typeof parsed.propertyDetails === "object") {
        additional.propertyDetails = parsed.propertyDetails;
        hasAdditional = true;
    }
    if (parsed.buildingPractitioners && Array.isArray(parsed.buildingPractitioners)) {
        additional.buildingPractitioners = parsed.buildingPractitioners;
        hasAdditional = true;
    }
    if (parsed.ownerBuilderSection && typeof parsed.ownerBuilderSection === "object") {
        additional.ownerBuilderSection = parsed.ownerBuilderSection;
        hasAdditional = true;
    }
    if (parsed.natureOfBuildingWork && typeof parsed.natureOfBuildingWork === "object") {
        additional.natureOfBuildingWork = parsed.natureOfBuildingWork;
        hasAdditional = true;
    }
    if (parsed.buildingDetails && typeof parsed.buildingDetails === "object") {
        additional.buildingDetails = parsed.buildingDetails;
        hasAdditional = true;
    }
    if (parsed.costOfBuildingWork && typeof parsed.costOfBuildingWork === "object") {
        additional.costOfBuildingWork = parsed.costOfBuildingWork;
        hasAdditional = true;
    }
    if (parsed.signatureDeclaration && typeof parsed.signatureDeclaration === "object") {
        additional.signatureDeclaration = parsed.signatureDeclaration;
        hasAdditional = true;
    }
    if (typeof parsed.applicantRole === "string") {
        additional.applicantRole = parsed.applicantRole;
        hasAdditional = true;
    }
    if (typeof parsed.applicantAcnArbn === "string") {
        additional.applicantAcnArbn = parsed.applicantAcnArbn;
        hasAdditional = true;
    }
    if (typeof parsed.recipient === "string") {
        additional.recipient = parsed.recipient;
        hasAdditional = true;
    }
    return hasAdditional ? additional : undefined;
}
function extractPropertyDetails(parsed) {
    if (!parsed.propertyDetails || typeof parsed.propertyDetails !== "object") {
        return undefined;
    }
    const pd = parsed.propertyDetails;
    return {
        buildingName: typeof pd.buildingName === "string" ? pd.buildingName : "",
        shopNo: typeof pd.shopNo === "string" ? pd.shopNo : "",
        fullAddress: typeof pd.fullAddress === "string" ? pd.fullAddress : "",
        lotNumber: typeof pd.lotNumber === "string" ? pd.lotNumber : "",
        lpPs: typeof pd.lpPs === "string" ? pd.lpPs : "",
        vol: typeof pd.vol === "string" ? pd.vol : "",
        fol: typeof pd.fol === "string" ? pd.fol : "",
        crownAllot: typeof pd.crownAllot === "string" ? pd.crownAllot : "",
        section: typeof pd.section === "string" ? pd.section : "",
        parish: typeof pd.parish === "string" ? pd.parish : "",
        county: typeof pd.county === "string" ? pd.county : "",
        council: typeof pd.council === "string" ? pd.council : "",
        landOwnedByCrownOrPublicAuthority: typeof pd.landOwnedByCrownOrPublicAuthority === "boolean" ? pd.landOwnedByCrownOrPublicAuthority : false,
    };
}
function extractBuilderNaturalPerson(parsed) {
    if (!parsed.builderNaturalPerson || typeof parsed.builderNaturalPerson !== "object") {
        return undefined;
    }
    const bnp = parsed.builderNaturalPerson;
    return {
        contactPerson: typeof bnp.contactPerson === "string" ? bnp.contactPerson : "",
        email: typeof bnp.email === "string" ? bnp.email : "",
        address: typeof bnp.address === "string" ? bnp.address : "",
        phone: typeof bnp.phone === "string" ? bnp.phone : "",
    };
}
function mapToFormData(raw, parsed, jobNumber, selectedSurveyor, additionalFormData) {
    const get = (k) => raw[k] ?? "";
    const getBool = (k) => raw[k] ?? false;
    const applicantRole = get("applicantRole");
    const propertyDetails = extractPropertyDetails(parsed);
    const builderNaturalPerson = extractBuilderNaturalPerson(parsed);
    return {
        jobNumber,
        applicantRole: (applicantRole === "owner" || applicantRole === "agent_of_owner") ? applicantRole : undefined,
        applicantAcnArbn: get("applicantAcnArbn") || undefined,
        applicant: {
            contactName: get("contactName"),
            email: get("email"),
            companyName: get("companyName"),
            phone: get("phone"),
            address: get("address"),
            documentAddress: get("addressForServing"),
        },
        propertyDetails,
        owner: {
            name: get("ownerName"),
            contactPerson: get("ownerContactPerson") || undefined,
            email: get("ownerEmail"),
            companyName: get("ownerCompanyName"),
            phone: get("ownerPhone"),
            address: get("ownerAddress"),
            acnArbn: get("acnArbn"),
        },
        tenant: {
            contactPerson: get("tenantContactPerson"),
            email: get("tenantEmail"),
            companyName: get("tenantCompanyName"),
            phone: get("tenantPhone"),
            address: get("tenantAddress"),
        },
        builder: {
            name: get("builderName") || undefined,
            contactPerson: get("builderContactPerson"),
            email: get("builderEmail"),
            companyName: get("builderCompanyName"),
            phone: get("builderPhone"),
            address: get("builderAddress"),
            practitionerNo: get("builderPractitionerNo") || undefined,
            acnArbn: get("builderAcnArbn") || undefined,
            postalAddress: get("builderPostalAddress") || undefined,
            naturalPerson: builderNaturalPerson,
        },
        lesseeResponsible: getBool("lesseeResponsible"),
        selectedSurveyor: selectedSurveyor || undefined,
        additionalFormData,
        additionalInformation: typeof parsed.additionalInformation === "string"
            ? parsed.additionalInformation
            : undefined,
    };
}
/**
 * Parses Building Permit Application data from job.notes.
 * Supports both Draft and Submitted formats.
 * Also extracts additional FormData fields if present (for forms saved with full editor).
 * Returns null if no valid Building Permit Application data is found.
 */
export function parseBuildingPermitFromNotes(notes, jobNumber = "") {
    const parsed = parseJsonFromNotes(notes, DRAFT_NOTES_PREFIX) ??
        parseJsonFromNotes(notes, SUBMITTED_NOTES_PREFIX);
    if (!parsed)
        return null;
    const raw = extractModalFormData(parsed);
    const hasAdditionalInfo = typeof parsed.additionalInformation === "string" &&
        parsed.additionalInformation.trim().length > 0;
    if (Object.keys(raw).length === 0 && !hasAdditionalInfo)
        return null;
    const selectedSurveyor = typeof parsed.selectedSurveyor === "string"
        ? parsed.selectedSurveyor
        : undefined;
    const additionalFormData = extractAdditionalFormData(parsed);
    return mapToFormData(raw, parsed, jobNumber, selectedSurveyor, additionalFormData);
}
/**
 * Extracts additionalInformation (Request for Additional Information from surveyor)
 * from job.notes. Parses Building Permit JSON (Draft or Submitted) and returns
 * the additionalInformation field if present.
 */
export function getAdditionalInformationFromNotes(notes) {
    const parsed = parseJsonFromNotes(notes, DRAFT_NOTES_PREFIX) ??
        parseJsonFromNotes(notes, SUBMITTED_NOTES_PREFIX);
    if (!parsed || typeof parsed.additionalInformation !== "string") {
        return null;
    }
    return parsed.additionalInformation.trim() || null;
}
/**
 * Merges additionalInformation into the Building Permit JSON in notes.
 * If Building Permit JSON exists, adds/updates additionalInformation and returns new notes.
 * If no Building Permit JSON exists, creates minimal Draft JSON with additionalInformation.
 */
export function mergeAdditionalInformationIntoNotes(notes, additionalInformation) {
    const trimmed = additionalInformation.trim();
    const parsed = parseJsonFromNotes(notes, DRAFT_NOTES_PREFIX) ??
        parseJsonFromNotes(notes, SUBMITTED_NOTES_PREFIX);
    if (parsed) {
        const updated = { ...parsed, additionalInformation: trimmed };
        const prefix = (notes ?? "").includes(SUBMITTED_NOTES_PREFIX)
            ? SUBMITTED_NOTES_PREFIX
            : DRAFT_NOTES_PREFIX;
        return `${prefix}${JSON.stringify(updated)}`;
    }
    return `${DRAFT_NOTES_PREFIX}${JSON.stringify({ additionalInformation: trimmed })}`;
}
